import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Lock, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '../context/CustomerAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(formatApiError(data.detail));
      setDone(true);
      toast.success('Password reset successful');
    } catch (err) {
      toast.error(err.message || 'Could not reset password');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-16 px-4">
      <Card className="w-full max-w-md shadow-xl" data-testid="reset-password-card">
        <CardHeader className="text-center">
          <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <Lock className="w-7 h-7 text-amber-600" />
          </div>
          <CardTitle className="text-2xl">Set a New Password</CardTitle>
        </CardHeader>
        <CardContent>
          {!token ? (
            <div className="text-center text-gray-600">
              <p>This reset link is invalid. Please request a new one from the login page.</p>
              <Button className="mt-4 bg-amber-600 hover:bg-amber-700" onClick={() => navigate('/login')}>Go to Login</Button>
            </div>
          ) : done ? (
            <div className="text-center space-y-4" data-testid="reset-success">
              <CheckCircle2 className="w-12 h-12 mx-auto text-green-500" />
              <p className="text-gray-700">Your password has been reset. You can now log in with your new password.</p>
              <Button className="w-full bg-amber-600 hover:bg-amber-700" onClick={() => navigate('/login')}>Go to Login</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">New Password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <Input data-testid="reset-password-input" type="password" className="pl-9" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Confirm Password</label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-3 text-gray-400" />
                  <Input data-testid="reset-confirm-input" type="password" className="pl-9" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter password" />
                </div>
              </div>
              <Button type="submit" disabled={submitting} data-testid="reset-submit-btn" className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                {submitting ? 'Please wait...' : 'Reset Password'}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordPage;
